import React from "react";
import { EkIcon } from "../EkIcon";

// "Value-stream heat" strip — the 12 ENTERPRISE360 value-stream icons tinted by
// their LIVE maturity RAG (from the blueprint's gap_analysis), turning the icon
// set into an at-a-glance health readout. Fully theme-aware (currentColor).
const SLUGS = [
  "strategy-to-execution", "idea-to-market", "lead-to-order", "order-to-cash",
  "plan-to-produce", "source-to-pay", "hire-to-retire", "record-to-report",
  "risk-to-assurance", "captive-finance", "enabling-data", "enabling-it",
];
const LABEL = {
  "strategy-to-execution": "Strategy→Exec", "idea-to-market": "Idea→Market",
  "lead-to-order": "Lead→Order", "order-to-cash": "Order→Cash",
  "plan-to-produce": "Plan→Produce", "source-to-pay": "Source→Pay",
  "hire-to-retire": "Hire→Retire", "record-to-report": "Record→Report",
  "risk-to-assurance": "Risk→Assurance", "captive-finance": "Captive Finance",
  "enabling-data": "Enabling Data", "enabling-it": "Enabling IT",
};

const slugify = (name) =>
  (name || "").split(" (")[0].trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

// RAG from maturity index (0-100)
const rag = (idx) => {
  if (idx == null) return { color: "#C7C7CF", band: "n/a", bg: "rgba(199,199,207,0.10)" };
  if (idx >= 70) return { color: "#059669", band: "Strong", bg: "rgba(5,150,105,0.10)" };
  if (idx >= 45) return { color: "#D97706", band: "Developing", bg: "rgba(217,119,6,0.10)" };
  return { color: "#DC2626", band: "At risk", bg: "rgba(220,38,38,0.10)" };
};

export default function ValueStreamHeatStrip({ bp }) {
  const rows = (bp?.report_sections?.gap_analysis) || bp?.gap_analysis || [];
  if (!rows.length) return null;
  const bySlug = {};
  rows.forEach((r) => { bySlug[slugify(r.name)] = r; });
  const active = new Set((bp?.active_value_streams || []).map((s) => slugify(typeof s === "string" ? s : s.name || s.id)));

  return (
    <div data-testid="e360-vs-heat-strip" className="rounded-xl border border-zinc-200 bg-white p-4">
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <div className="text-[12px] tracking-[0.14em] uppercase text-[#7737F6] font-semibold">Value-stream heat</div>
        <div className="flex items-center gap-3 text-[10px] text-zinc-500">
          <span className="flex items-center gap-1"><i className="h-2 w-2 rounded-full" style={{ background: "#059669" }} /> Strong ≥70</span>
          <span className="flex items-center gap-1"><i className="h-2 w-2 rounded-full" style={{ background: "#D97706" }} /> Developing 45–69</span>
          <span className="flex items-center gap-1"><i className="h-2 w-2 rounded-full" style={{ background: "#DC2626" }} /> At risk &lt;45</span>
        </div>
      </div>
      <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-12 gap-2">
        {SLUGS.map((slug) => {
          const row = bySlug[slug];
          const idx = row ? row.current_index : null;
          const r = rag(idx);
          const dim = !row || (active.size > 0 && !active.has(slug) && idx == null);
          return (
            <div key={slug} data-testid={`e360-vs-heat-${slug}`}
                 title={`${LABEL[slug]}${idx != null ? ` — ${idx}/100 · ${r.band}` : " — not assessed"}`}
                 className="flex flex-col items-center text-center rounded-lg py-2.5 px-1 transition-colors"
                 style={{ background: r.bg, opacity: dim ? 0.4 : 1 }}>
              <span style={{ color: r.color }}><EkIcon name={slug} className="w-6 h-6" /></span>
              <span className="mt-1.5 text-[9.5px] leading-tight text-zinc-600 font-medium">{LABEL[slug]}</span>
              <span className="text-[11px] font-semibold tabular-nums" style={{ color: r.color }}>
                {idx != null ? idx : "—"}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
